// @ts-nocheck
import { useMemo } from 'react';
import type { AnalysisData, ClientWorkspaceCompany } from '../../../services/AnalysisApiClient';
import { useCompanyContext } from '../context/CompanyContext';
import { useClientWorkspaceCompanyCard } from './useClientWorkspaceCompanyCard';
import { useClientWorkflowSummaryBadges } from './useClientWorkflowSummaryBadges';

/** Merge workspace company name/closing into form-shaped data when the form fields are blank. */
function mergeWorkspaceFallback<T extends { companyName?: string; closingDate?: string }>(
  base: T,
  workspaceCompany: ClientWorkspaceCompany | null
): T {
  return {
    ...base,
    companyName: base.companyName || workspaceCompany?.companyName,
    closingDate: base.closingDate || workspaceCompany?.closingDate,
  };
}

/**
 * Summary badges for client analyses, run history, and workflow overview.
 * Pass `formData` on the workflow page; list pages resolve from context + workspace company.
 */
export function useClientPageSummaryBadges(formDataOverride?: AnalysisData | null) {
  const { data, archivedAnalyses, analyses, currentAnalysisId } = useCompanyContext();
  const { workspaceCompany } = useClientWorkspaceCompanyCard(archivedAnalyses);
  const badgeFormData = useMemo(() => {
    if (formDataOverride != null) {
      return mergeWorkspaceFallback(formDataOverride, workspaceCompany);
    }
    const active = analyses.find((a) => a.id === currentAnalysisId);
    const linked = analyses.find((a) => a.lawFirmId && a.companyId && String(a.data?.companyName || '').trim());
    const base = active?.data ?? linked?.data ?? data ?? {};
    return mergeWorkspaceFallback(base, workspaceCompany);
  }, [formDataOverride, analyses, currentAnalysisId, data, workspaceCompany]);

  const summaryBadges = useClientWorkflowSummaryBadges(badgeFormData, archivedAnalyses);

  return { summaryBadges };
}
